import { Diagnostic } from "@ionic-native/diagnostic";
import { LocationAccuracy } from "@ionic-native/location-accuracy";
import { isCapacitorPlatform } from "@src/appV2/lib";
import { getDeviceGeoLocation } from "@src/appV2/Location";

export enum LocationAlertType {
  CANNOT_REQUEST = "CANNOT_REQUEST",
  USER_DENIED = "USER_DENIED",
  COORDS_NOT_AVAILABLE = "COORDS_NOT_AVAILABLE",
  NOT_CLOSE = "NOT_CLOSE",
  PLUGIN_ERROR = "PLUGIN_ERROR",
}

export enum LocationType {
  LIVE = "LIVE",
}

export enum AppType {
  MOBILE = "MOBILE",
  WEB = "WEB",
}

export enum ConnectionMode {
  ONLINE = "ONLINE",
  OFFLINE = "OFFLINE",
}

export const LocationAlertTypeReason = {
  CANNOT_REQUEST: "We cannot request location",
  USER_DENIED: "User denied access to location",
  COORDS_NOT_AVAILABLE: "Location isn't available",
  NOT_CLOSE: "User is not close to the workplace",
  PLUGIN_ERROR: "Error in the plugin during runtime",
};

// a lot of location-related errors come from third-party libraries with no typing and the source code is difficult to understand
// so we just play it safe and get whatever we can to add to the logs
export const getUnknownErrorString = (error: unknown) => {
  const errorString = error as Error | string;
  return errorString?.toString?.();
};

export const isGeoLocationEnabled = async (): Promise<boolean> => {
  try {
    if (isCapacitorPlatform()) {
      return await Diagnostic.isLocationEnabled();
    }

    await getDeviceGeoLocation();
    return true;
  } catch {
    return false;
  }
};

export const enableGeoLocation = async (): Promise<{
  success: boolean;
  error?: LocationAlertType;
  errorDetails?: string;
}> => {
  try {
    if (isCapacitorPlatform()) {
      await LocationAccuracy.request(LocationAccuracy.REQUEST_PRIORITY_HIGH_ACCURACY);
    } else {
      // Web platform
      const perm = await navigator.permissions.query({ name: "geolocation" });
      switch (perm.state) {
        case "granted": {
          return { success: true };
        }

        case "denied": {
          return { success: false, error: LocationAlertType.USER_DENIED };
        }

        case "prompt": {
          await getDeviceGeoLocation({
            timeoutInMilliseconds: 60_000,
            maximumAgeInMilliseconds: 60_000,
          });
          break;
        }

        default: {
          throw new Error("Unexpected navigator.permissions.query state");
        }
      }
    }

    return { success: true };
  } catch (error) {
    return {
      success: false,
      error: LocationAlertType.PLUGIN_ERROR,
      errorDetails: getUnknownErrorString(error),
    };
  }
};
